import React, {createRef, useState} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import _ from "underscore";
import moment from "moment";
import MaterialTable from "@material-table/core";
import { TablePagination, useTheme } from "@material-ui/core";
import fileDialog from "file-dialog";
import axios from "axios";

import { Alert, MaskedText, MaskedNumber, DatePicker } from "../../../controls";
import { getApiSitePrefix } from "../../../functions/SiteDefaults";
import readFileAsync from "../../../functions/ReadFileAsync";
import {
    useExchangeRatesWithName,
    useAddExchangeRate,
    useUpdateExchangeRate,
    useDeleteExchangeRate,
    useCurrencies
} from "../../../services/currencies";
import CurrencySelector from './CurrencySelector';
import { defaultComponents } from '../../../controls/Material-Table-Components';

const apiSitePrefix = getApiSitePrefix();

const getExchangeColumns = (props, currencies) => {

    const baseColumn = {
        cellStyle: { padding: "8px 2px" },
        headerStyle: { padding: "8px 2px" },
        editable: props.canEdit ? "always" : "never",
        emptyValue: "",
        initialEditValue: "",
    };

    const formatChars={
        "U": "[A-Z]",
        "A": "[A-Z0-9]",
        "a": "[A-Za-z0-9]",
        "9": "[0-9]",
        "D": "[0-9.]",
        "-": "-",
        "x": "."
    }
    const defaultEditProps = {
        style:{fontSize: "0.875rem"}
    };

    return [
        {
            ...baseColumn,
            cellStyle: { padding: "8px 2px 8px 12px" },
            headerStyle: { padding: "8px 2px 8px 12px" },
            title: "Code",
            field: "currencyCode",
            width: 50,            
            initialEditValue: "",
            editable: props.canEdit ? "onAdd" : "never",
            editComponent: (props) => (
            <CurrencySelector
                value={props.value}
                returnType="object"
              onChange={currency => {
                let data = { ...props.rowData };
                data.currencyCode = currency.data.currencyCode;
                data.currencyName = currency.data.currencyName;
                props.onRowDataChange(data);
              }}
            />
          )
        },
        {
          ...baseColumn,
          title: "Name",
          field: "currencyName",
          editable: "never",
          editComponent: (props) => (
            <MaskedText
              mask={"x".repeat(50)}
              maskChar={""}
              alwaysShowMask={false}
              formatChars={formatChars}
              value={props.value}
              onChange={props.onChange}
              inputProps={defaultEditProps}
            />
          )
        },
        {
          ...baseColumn,
          title: "Valid From",
          field: "validFrom",
          type: "numeric",
          emptyValue: "",
          initialEditValue: props.initialDate,
          editable: props.canEdit ? "onAdd" : "never",
          editComponent: (props) => (
            <DatePicker
              minDate={props.initialDate}
              value={props.value}
              disableToolbar={true}
              autoOk={true}
              format={props.dateFormat}
              onChange={props.onChange}
            />
          )
        },
        {
          ...baseColumn,
          title: "Exchange Rate",
          field: "rate",
          type: 'numeric',
          editComponent: (props) => (
            <MaskedNumber
              value={props.value}
              decimalScale={9}
              fixedDecimalScale={true}
              onChange={props.onChange}
              style={{ fontSize: "0.875rem", textAlign: "right" }}
            />
          ),
          render: (props) => (
            <MaskedNumber
              displayType="text"
              value={props.rate}
              decimalScale={9}
              fixedDecimalScale={true}
//              style={{ fontSize: "0.875rem", textAlign: "right" }}
            />
          )
        },
        {
          ...baseColumn,
          title: "Inverse Rate",
          field: "inverseRate",
          type: 'numeric',
          editComponent: (props) => (
            <MaskedNumber
              value={props.value}
              decimalScale={9}
              fixedDecimalScale={true}
              onChange={props.onChange}
              style={{ fontSize: "0.875rem", textAlign: "right" }}
            />
          ),
          render: (props) => (
            <MaskedNumber
              displayType="text"
              value={props.inverseRate}
              decimalScale={9}
              fixedDecimalScale={true}
//              style={{ fontSize: "0.875rem", textAlign: "right" }}
            />
          )
        },
        {
          ...baseColumn,
          title: "Updated By",
          field: "enteredBy",
          cellStyle: { padding: "8px 2px 8px 12px" },
          headerStyle: { padding: "8px 2px 8px 12px" },

          render: (props) => {
            const validFrom = props.updatedAt ? "on " + moment(props.updatedAt, "YYYY-MM-DD HH:mm:ss").format("DD/MM/YYYY") : "";
            return (
              <div style={{ width: 240 }}>
                  {props.enteredBy}&nbsp;{validFrom}
              </div>
            )
          },
        },
    ];
};

const ExchangeRateList = (_props) => {
  const theme = useTheme();
  const { isAuthenticated } = useAuth0();

  // canEdit should be passed from parent in _props
  const props = {
    ..._props
  }
  const [state, setState] = useState({
    canEdit: props.canEdit,
    childCurrency: false
  });

  const [alert, setAlert] = useState({
    open: false,
    severity: "error",
    message: ""
  });

  const month = props.month;
  const year = props.year;
  const rateProviderCode = props.rateProviderCode ? props.rateProviderCode : "SYSTEM";
  const tableRef = createRef({ state: { lastEditingRow: false } });

  const tabFirstDayOfMonth = moment(`${year}-${month}-01`, "YYYY-MM-DD");
  const initialDate = tabFirstDayOfMonth.toISOString();
  const dateFormat = "dd/MM/yyyy";


  const handleAddExchangeRate = useAddExchangeRate();
  const handleUpdateExchangeRate = useUpdateExchangeRate();
  const handleDeleteExchangeRate = useDeleteExchangeRate();

  const queryClient = useQueryClient();
  const extraKeys = [rateProviderCode, year, month];
  const exchangeRates = useExchangeRatesWithName({tableRef, queryClient, extraKeys});
  // currencies is for the currencySelector
  const currencies = useCurrencies({tableRef, queryClient});

  const uploadRatesToServer = async (rateProviderCode, file) => {
    const fileContent = await readFileAsync(file);
    const buffer = new Uint8Array(fileContent);
    const dataArray = [];
    buffer.forEach((x,i)=>{dataArray[i] = x});

    const headers = {...axios.defaults.headers, 'Content-Type': 'application/json'};

    try {
      const response = await axios.post(
        `${apiSitePrefix}/exchangerates/upload/${rateProviderCode}/`,
        { xlsx: JSON.stringify(dataArray) },
        {
          headers,
        }
      );

      console.error(response);
      if (response && response.data) {
        if (response.data.status) {
          setAlert({ severity: "info", open: true, message: "Upload completed successfully", timeOut: 10 });
        } else {
          setAlert({ severity: "error", open: true, message: "The Excel file failed to upload with this error: " + response.data.error });
        }
      } else {
        setAlert({ severity: "error", open: true, message: "The Excel file failed to upload due to an unknown error " });
      }
      queryClient.invalidateQueries(['ExchangeRates', ...extraKeys]);
    } catch (err) {
      console.error(err);
      setAlert({ severity: "error", open: true, message: "The Excel file failed to upload with this error: " + err.message });
    }
  }
  const uploadExchangeRates = async () => {

    try {   
      const files = await fileDialog({ accept: '.xlsx' });
      if (files && files.length > 0) {
        const fileName = files[0].name + "'";
        // console.log("selected file: ", fileName);
        uploadRatesToServer(rateProviderCode, files[0]);
      }
    } catch (err) {
      setAlert({ severity: "error", open: true, message: "Failed to upload file!" });
    }
  };

  return (isAuthenticated &&
    <div style={{width: "100%", marginLeft: 20, marginRight: 20}}>
      <Alert
        onChange={(open) => {
          setAlert({ ...alert, open })
        }}
        open={alert.open}
        severity={alert.severity}
        message={alert.message}
        timeOut={alert.timeOut || 0}
      />
      <MaterialTable
          tableRef={tableRef}
          style={{
              border: "none",
              boxShadow: "none",
              borderRadius: 0,
          }}
      options={{
              actionsColumnIndex: -1,
              cellStyle: {
                  padding: "8px 2px",
              },
              headerStyle: {
                backgroundColor: theme.palette.tableHeader.backgroundColor,
                color: theme.palette.tableHeader.textColor,
                padding: "2px",
              },
              toolbarButtonAlignment: "right",
              pageSize: 10,
              detailPanelType: "single",
          }}
          actions={state.canEdit?[
              {
                  icon: 'upload',
                  tooltip: 'Upload Exchange Rates',
                  isFreeAction: true,
                  iconProps: { style: { color: theme.palette.primary.main } },
              onClick: ()=> {uploadExchangeRates()} 
              }
          ]:undefined}
          components={{
              ...defaultComponents,
              Pagination: (props) => (
                  <TablePagination
                      {...props}
                      rowsPerPageOptions={[5, 10, 15]}
                  />
              ),
          }}
          title="Exchange Rates"
          columns={getExchangeColumns({canEdit: state.canEdit, initialDate, dateFormat }, currencies)}
          data={exchangeRates.data}
          editable={{
              isEditable: () => {
                  return state.canEdit;
              },
              isDeletable: () => {
                  return state.canEdit;
              },
              onRowAdd: state.canEdit
                  ? (newData) => new Promise((resolve, reject) => {
                      handleAddExchangeRate.mutate({ record: { ...newData, rateProviderCode }, resolve, reject, extraKeys});
                  })
                  : undefined,
              onRowUpdate: (newData, oldData) =>
                  new Promise((resolve, reject) => {
                      handleUpdateExchangeRate.mutate({record: newData, oldRecord: oldData, resolve, reject, extraKeys});
                  }),
              onRowDelete: (oldData) =>
                  new Promise((resolve, reject) => {
                      handleDeleteExchangeRate.mutate({oldRecord: oldData, resolve, reject, extraKeys});
                  }),
          }}
      />
    </div>
  );
}

export default ExchangeRateList;
